// BA Roster → who holds the Brand Ambassador role (BA P1.10, WP-2d).
//
// "Make Brand Ambassador" grants the role to an employee; "Remove Brand
// Ambassador role" revokes it. Removing changes no profile rows server-side, so
// the reviewer is ASKED "Also unpublish their portfolio?" — with no default:
// both answers look alike, neither is focused, and Escape / Cancel / clicking
// away cancel the whole removal. Yes → revoke, then the existing unpublish
// (reason `provider`); No → revoke only. Takes effect on the person's next
// access token, at most 15 minutes, and the screen says so.
//
// Presentation only: auth-service gates the role endpoints by
// ba:profile:review in the same company and refuses a self-grant.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState } from '@/features/shared/components/ui/DataState';
import { PeoplePicker, type Person, type EligiblePage } from '@/features/shared/components/PeoplePicker';
import { useChoice } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { searchEmployees } from '@/services/workforceService';
import { unpublishBaProfile } from '@/services/baProfileService';
import {
  type BaRoleHolder, grantBaRole, listBaRoleHolders, publishedProfileForHolder, revokeBaRole,
} from '@/services/baRoleService';

type Load = { state: 'loading' } | { state: 'ok'; holders: BaRoleHolder[] } | { state: 'denied' | 'unavailable' } |
  { state: 'error'; message: string };

export const BaRoleHolders: React.FC<{ onChange?: () => void }> = ({ onChange }) => {
  const { t } = useTranslation('brandAmbassadors');
  const k = (key: string, defaultValue: string, o: Record<string, unknown> = {}) =>
    t(`role.${key}`, { defaultValue, ...o }) as string;
  const choose = useChoice();
  const myUserId = useAppSelector((s: any) => s.auth?.user?.id) as string | undefined;
  const [list, setList] = useState<Load>({ state: 'loading' });
  const [person, setPerson] = useState<Person | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setList({ state: 'loading' });
    listBaRoleHolders()
      .then((holders) => setList({ state: 'ok', holders }))
      .catch((e: any) => setList(
        e?.response?.status === 403 ? { state: 'denied' }
          : e?.response?.status === 404 ? { state: 'unavailable' }
            : { state: 'error', message: getApiErrorMessage(e, k('errors_list', 'Could not load who holds the role.')) },
      ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const holderIds = list.state === 'ok' ? new Set(list.holders.map((h) => h.userId)) : new Set<string>();

  // The directory search returns each employee's login (user_id) next to the
  // employee record: the role is granted to the AUTH USER (WP-2b's {userId}),
  // never the employee id. An employee without a
  // login cannot hold a role; nobody can grant it to themselves.
  const fetchPage = async (q: string): Promise<EligiblePage> => {
    const hits = q.trim() ? await searchEmployees(q) : [];
    const items: Person[] = hits
      .filter((h) => !!h.user_id && h.user_id !== myUserId && !holderIds.has(h.user_id))
      .map((h) => ({ employee_id: h.employee_uuid || h.id, user_id: h.user_id, display_name: h.name, job_title: h.sublabel || null }));
    return { items, total: items.length, page: 1, page_size: items.length || 10, has_more: false, reason: 'OK', scope_summary: '' };
  };

  const changed = () => { load(); onChange?.(); };

  const make = async () => {
    if (!person?.user_id) return;
    setBusy(true);
    try {
      const res = await grantBaRole(person.user_id);
      toast.success(res?.changed === false
        ? k('toast_already_granted', '{{name}} is already a Brand Ambassador.', { name: person.display_name })
        : k('toast_granted', '{{name}} is now a Brand Ambassador. Takes effect within 15 minutes.', { name: person.display_name }));
      setPerson(null);
      changed();
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('errors_grant', 'Could not make them a Brand Ambassador.')));
    } finally { setBusy(false); }
  };

  const remove = async (h: BaRoleHolder) => {
    const answer = await choose({
      title: k('remove_question', 'Also unpublish their portfolio?'),
      message: k('remove_message', 'Removing the Brand Ambassador role from {{name}}. Takes effect within 15 minutes.', { name: h.name || '—' }),
      noDefault: true,
      choices: [
        { value: 'yes', label: k('remove_yes', 'Yes, unpublish it'), description: k('remove_yes_hint', 'Clients stop seeing their portfolio at once.') },
        { value: 'no', label: k('remove_no', 'No, leave it published'), description: k('remove_no_hint', 'It stays in the catalogue until a reviewer unpublishes it.') },
      ],
      cancelLabel: k('cancel', 'Cancel'),
    });
    if (answer !== 'yes' && answer !== 'no') return; // cancelled: nothing at all

    setBusy(true);
    try {
      try {
        await revokeBaRole(h.userId);
      } catch (e) {
        toast.error(getApiErrorMessage(e, k('errors_revoke', 'Could not remove the role.')));
        return;
      }
      if (answer === 'no') {
        toast.success(k('toast_revoked', 'Role removed. Takes effect within 15 minutes.'));
        return;
      }
      try {
        const live = await publishedProfileForHolder(h);
        if (!live) {
          toast.success(k('toast_revoked_nothing_live', 'Role removed. They had no portfolio in the catalogue. Takes effect within 15 minutes.'));
          return;
        }
        await unpublishBaProfile(live.id, 'provider');
        toast.success(k('toast_revoked_unpublished', 'Role removed and portfolio unpublished. Takes effect within 15 minutes.'));
      } catch (e) {
        toast.error(k('errors_unpublish_after_revoke', 'Role removed, but the portfolio is still published: {{msg}}', {
          msg: getApiErrorMessage(e, k('errors_unpublish', 'Could not unpublish the portfolio.')),
        }));
      }
    } finally {
      setBusy(false);
      changed();
    }
  };

  return (
    <Card data-testid="ba-role-holders">
      <CardHeader>
        <CardTitle>{k('title', 'Who is a Brand Ambassador')}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {k('subtitle', 'Only Brand Ambassadors can build a portfolio. Takes effect within 15 minutes.')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start">
          <PeoplePicker
            id="ba-role-picker"
            className="md:flex-1"
            value={person}
            onChange={setPerson}
            fetchPage={fetchPage}
            disabled={busy}
            placeholder={k('picker_placeholder', 'Search employees…')}
            emptyHint={k('picker_hint', 'Only employees who can sign in can be made Brand Ambassadors.')}
            aria-label={k('picker_placeholder', 'Search employees…')}
          />
          <Button disabled={busy || !person?.user_id} onClick={make}>
            <UserPlus className="w-4 h-4 mr-1" />{k('make', 'Make Brand Ambassador')}
          </Button>
        </div>

        {list.state === 'loading' && <DataState variant="loading" />}
        {list.state === 'denied' && <DataState variant="denied" />}
        {list.state === 'unavailable' && <DataState variant="unavailable" message={k('unavailable', 'Brand Ambassador roles can’t be managed here yet.')} />}
        {list.state === 'error' && <DataState variant="error" message={list.message} onRetry={load} />}
        {list.state === 'ok' && list.holders.length === 0 && (
          <DataState variant="empty" title={k('empty', 'Nobody holds the Brand Ambassador role yet.')} />
        )}
        {list.state === 'ok' && list.holders.length > 0 && (
          <ul className="divide-y divide-border">
            {list.holders.map((h) => (
              <li key={h.userId} data-testid={`ba-holder-${h.userId}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span>
                  <span className="block text-sm font-medium text-foreground">{h.name || '—'}</span>
                  {h.since && (
                    <span className="block text-xs text-muted-foreground">
                      {k('since', 'Since {{date}}', { date: new Date(h.since).toLocaleDateString() })}
                    </span>
                  )}
                </span>
                <Button size="sm" variant="outline" className="text-danger" disabled={busy} onClick={() => remove(h)}>
                  {k('remove', 'Remove Brand Ambassador role')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default BaRoleHolders;
